import React from 'react';
import { useScrollReveal } from '../hooks/useScrollReveal';

/**
 * ScrollReveal Component - Wraps children with scroll reveal animation
 * @param {Object} props - Component props
 * @param {React.ReactNode} props.children - Child elements to animate
 * @param {string} props.animation - Animation type ('up', 'down', 'left', 'right', 'scale', 'rotate')
 * @param {number} props.delay - Delay before animation starts (in ms)
 * @param {number} props.threshold - Intersection threshold (0-1)
 * @param {string} props.rootMargin - Root margin for intersection observer
 * @param {boolean} props.triggerOnce - Whether to trigger animation only once
 * @param {string} props.speed - Animation speed ('fast', 'normal', 'slow')
 * @param {number} props.stagger - Stagger delay for multiple children
 * @param {string} props.className - Additional CSS classes
 * @param {string} props.as - HTML element type (default: 'div')
 */
const ScrollReveal = ({
  children,
  animation = 'up',
  delay = 0,
  threshold = 0.1,
  rootMargin = '0px 0px -50px 0px',
  triggerOnce = true,
  speed = 'normal',
  stagger = 0,
  className = '',
  as: Component = 'div',
  ...props
}) => {
  const { ref } = useScrollReveal({
    threshold,
    rootMargin,
    triggerOnce,
    delay,
  });

  // Build CSS classes
  const getAnimationClass = () => {
    switch (animation) {
      case 'up':     return 'reveal-up';
      case 'down':   return 'reveal-down';
      case 'left':   return 'reveal-left';
      case 'right':  return 'reveal-right';
      case 'scale':  return 'reveal-scale';
      case 'rotate': return 'reveal-rotate';
      case 'blur':   return 'reveal-blur';
      case 'glow':   return 'reveal-glow';
      default:       return 'reveal';
    }
  };

  const getSpeedClass = () => {
    switch (speed) {
      case 'fast':
        return 'reveal-fast';
      case 'slow':
        return 'reveal-slow';
      default:
        return '';
    }
  };

  const getStaggerClass = () => {
    if (stagger > 0 && stagger <= 6) {
      return `reveal-stagger-${stagger}`;
    }
    return '';
  };

  const classes = [
    getAnimationClass(),
    getSpeedClass(),
    getStaggerClass(),
    className,
  ].filter(Boolean).join(' ');

  return (
    <Component ref={ref} className={classes} {...props}>
      {children}
    </Component>
  );
};

/**
 * Pre-configured ScrollReveal variants for common use cases
 */
export const RevealUp = (props) => <ScrollReveal animation="up" {...props} />;
export const RevealDown = (props) => <ScrollReveal animation="down" {...props} />;
export const RevealLeft = (props) => <ScrollReveal animation="left" {...props} />;
export const RevealRight = (props) => <ScrollReveal animation="right" {...props} />;
export const RevealScale = (props) => <ScrollReveal animation="scale" {...props} />;
export const RevealRotate = (props) => <ScrollReveal animation="rotate" {...props} />;

/**
 * Fast reveal variants
 */
export const FastReveal = (props) => <ScrollReveal speed="fast" {...props} />;
export const SlowReveal = (props) => <ScrollReveal speed="slow" {...props} />;

/**
 * Staggered reveal for multiple items
 */
export const StaggeredReveal = ({ children, staggerDelay = 100, ...props }) => {
  return (
    <>
      {React.Children.map(children, (child, index) => (
        <ScrollReveal
          key={index}
          stagger={Math.min(index + 1, 6)} // Max 6 stagger classes
          {...props}
        >
          {child}
        </ScrollReveal>
      ))}
    </>
  );
};

export default ScrollReveal;